// Static Arrival Time Distribution profile catalogue driving the "Profile Name"
// dropdown for Arrival Distribution — no longer sourced from the distributions API.
export const ARRIVAL_DISTRIBUTION_PROFILES = [
  {
    id: "normal_morning",
    name: "Normal in the morning",
    type: "normal",
    parameters: { mean: 8, sigma: 2, min: 4, max: 12 },
  },
  {
    id: "normal_midday",
    name: "Normal at midday",
    type: "normal",
    parameters: { mean: 12, sigma: 2, min: 8, max: 16 },
  },
  {
    id: "normal_afternoon",
    name: "Normal in the afternoon",
    type: "normal",
    parameters: { mean: 16, sigma: 2, min: 12, max: 20 },
  },
  {
    id: "uniform_morning",
    name: "Uniform in the morning",
    type: "uniform",
    parameters: { min: 6, max: 10 },
  },
  {
    id: "uniform_midday",
    name: "Uniform at midday",
    type: "uniform",
    parameters: { min: 10, max: 14 },
  },
  {
    id: "uniform_afternoon",
    name: "Uniform in the afternoon",
    type: "uniform",
    parameters: { min: 14, max: 18 },
  },
  {
    id: "triangular_morning",
    name: "Triangular in the morning",
    type: "triangular",
    parameters: { min: 6, mode: 9, max: 12 },
  },
  {
    id: "triangular_midday",
    name: "Triangular at midday",
    type: "triangular",
    parameters: { min: 10, mode: 13, max: 16 },
  },
  {
    id: "triangular_afternoon",
    name: "Triangular in the afternoon",
    type: "triangular",
    parameters: { min: 14, mode: 17, max: 20 },
  },
  {
    id: "lognormal_morning",
    name: "Log Normal in the morning",
    type: "shiftedLognormal",
    parameters: { shift: 8, mu: 0.5, sigma: 1 },
  },
  {
    id: "lognormal_midday",
    name: "Log Normal at midday",
    type: "shiftedLognormal",
    parameters: { shift: 12, mu: 0.5, sigma: 1 },
  },
  {
    id: "lognormal_afternoon",
    name: "Log Normal in the afternoon",
    type: "shiftedLognormal",
    parameters: { shift: 16, mu: 0.5, sigma: 1 },
  },
  {
    id: "extreme_morning",
    name: "Extreme value in the morning",
    type: "reverseGumbel",
    parameters: { location: 8.3693, scale: 1.01525 },
  },
  {
    id: "extreme_midday",
    name: "Extreme value at midday",
    type: "reverseGumbel",
    parameters: { location: 12.3693, scale: 1.01525 },
  },
  {
    id: "extreme_afternoon",
    name: "Extreme value in the afternoon",
    type: "reverseGumbel",
    parameters: { location: 16.3693, scale: 1.01525 },
  },
];

export function normalPdf(x, mean, sigma) {
  return (
    Math.exp(-0.5 * Math.pow((x - mean) / sigma, 2)) /
    (sigma * Math.sqrt(2 * Math.PI))
  );
}

export function uniformPdf(x, min, max) {
  return x >= min && x <= max ? 1 / (max - min) : 0;
}

export function triangularPdf(x, min, mode, max) {
  if (x < min || x > max) {
    return 0;
  }

  if (x <= mode) {
    return (2 * (x - min)) / ((max - min) * (mode - min));
  }

  return (2 * (max - x)) / ((max - min) * (max - mode));
}

export function shiftedLognormalPdf(x, shift, mu, sigma) {
  const z = x - shift;

  if (z <= 0) {
    return 0;
  }

  return (
    Math.exp(-0.5 * Math.pow((Math.log(z) - mu) / sigma, 2)) /
    (z * sigma * Math.sqrt(2 * Math.PI))
  );
}

export function reverseGumbelPdf(x, location, scale) {
  const z = (x - location) / scale;

  return Math.exp(z - Math.exp(z)) / scale;
}

export function calculateArrivalDistribution(profile) {
  const xValues = Array.from({ length: 96 }, (_, index) => index * 0.25);

  const rawValues = xValues.map((hour) => {
    switch (profile.type) {
      case "normal": {
        const { mean, sigma, min, max } = profile.parameters;

        if (hour < min || hour > max) {
          return 0;
        }

        return normalPdf(hour, mean, sigma);
      }

      case "uniform":
        return uniformPdf(
          hour,
          profile.parameters.min,
          profile.parameters.max,
        );

      case "triangular":
        return triangularPdf(
          hour,
          profile.parameters.min,
          profile.parameters.mode,
          profile.parameters.max,
        );

      case "shiftedLognormal":
        return shiftedLognormalPdf(
          hour,
          profile.parameters.shift,
          profile.parameters.mu,
          profile.parameters.sigma,
        );

      case "reverseGumbel":
        return reverseGumbelPdf(
          hour,
          profile.parameters.location,
          profile.parameters.scale,
        );

      default:
        throw new Error(`Unsupported arrival distribution: ${profile.type}`);
    }
  });

  const total = rawValues.reduce((sum, value) => sum + value, 0);
  const probability = rawValues.map((value) =>
    total > 0 ? (value / total) * 100 : 0,
  );

  return { x: xValues, probability };
}
